import { useState, useEffect, useCallback, useRef } from 'react';
import { format } from 'date-fns';
import { Link, useNavigate } from 'react-router-dom';
import { apiGet, apiPost, apiPut, apiDelete } from '@/utils/api';
import { useBadgeChecker } from '@/hooks/useBadgeChecker';
import { getDailyQuote, getRandomQuote } from '@/utils/bossQuotes';
import { playCashRegister, playSuccess, playCelebration } from '@/utils/audio';
import { TransactionCelebration } from '@/components/Celebrations';
import { GlassCard } from '@/components/ui/GlassCard';
import { QuickHelp, PAGE_TIPS } from '../components/HelpComponents';
import { GuapoNudge } from '@/components/GuapoNudge';
import { GuapBoard } from '@/components/GuapBoard';
import { useEnvelope } from '@/contexts/EnvelopeContext';
import CreditTips from '@/components/CreditTips';
import HabitPraise from '@/components/HabitPraise';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';
import { Info } from 'lucide-react';
import { BrokenStreakDrama } from '@/components/BrokenStreakDrama';
import { HouseholdMilestoneCelebration } from '@/components/HouseholdMilestoneCelebration';
import { GuapoMascot } from '@/components/GuapoMascot';

// Boss Talk - Daily Quote Component
function BossTalkCard() {
  const quote = getDailyQuote();
  
  return (
    <div className="bg-gradient-to-r from-gray-900 to-gray-800 rounded-xl p-4 text-white relative overflow-hidden">
      <div className="absolute top-0 right-0 text-6xl opacity-10 -mr-2 -mt-2">💬</div>
      <div className="relative z-10">
        <div className="flex items-center gap-2 mb-2">
          <span className="text-xs font-bold uppercase tracking-wider text-yellow-400">Boss Talk</span>
          <span className="text-xs text-gray-400">Daily Motivation</span>
        </div>
        <p className="text-lg font-medium mb-2 leading-snug">"{quote.quote}"</p>
        <p className="text-sm text-gray-400">— {quote.author}</p>
      </div>
    </div>
  );
}

// Toast notification component
function Toast({ message, type, onClose }) {
  useEffect(() => {
    const timer = setTimeout(onClose, 4000);
    return () => clearTimeout(timer);
  }, [onClose]);

  const bgColor = type === 'success' ? 'bg-green-500' : type === 'warning' ? 'bg-yellow-500' : 'bg-blue-500';

  return (
    <div 
      className={`fixed top-4 right-4 ${bgColor} text-white px-4 py-3 rounded-lg shadow-lg z-50 animate-slide-in`}
      role="alert"
      aria-live="assertive"
      aria-atomic="true"
    >
      <p className="font-medium">{message}</p>
    </div>
  );
}

// Alert Card Component
function AlertCard({ alert, onDismiss }) {
  return (
    <div 
      className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3"
      role="alert"
      aria-live="polite"
    >
      <span className="text-xl" aria-hidden="true">{alert.icon}</span>
      <div className="flex-1">
        <h4 className="font-semibold text-gray-900">{alert.title}</h4>
        <p className="text-sm text-gray-700 mt-1">{alert.description}</p>
      </div>
      <div className="flex items-center gap-2">
        <button 
          className="text-sm text-amber-700 hover:text-amber-900 font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 rounded"
          aria-label={`${alert.action_label} for ${alert.title}`}
        >
          {alert.action_label}
        </button>
        <button 
          onClick={() => onDismiss(alert)}
          className="text-gray-400 hover:text-gray-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-500 rounded p-1"
          aria-label={`Dismiss ${alert.title} alert`}
        >
          <span aria-hidden="true">✕</span>
        </button>
      </div>
    </div>
  );
}

// Action Card Component
function ActionCard({ action, amount, expanded, onExpand, onApply }) {
  const [allocateAmount, setAllocateAmount] = useState(amount);
  const cardId = `action-card-${action.id}`;
  const inputId = `allocate-amount-${action.id}`;

  const bgColors = {
    debt: 'hover:bg-blue-50 border-blue-200',
    savings: 'hover:bg-green-50 border-green-200',
    invest: 'hover:bg-purple-50 border-purple-200',
    enjoy: 'hover:bg-yellow-50 border-yellow-200'
  };

  const selectedBg = {
    debt: 'bg-blue-50 border-blue-400',
    savings: 'bg-green-50 border-green-400',
    invest: 'bg-purple-50 border-purple-400',
    enjoy: 'bg-yellow-50 border-yellow-400'
  };

  return (
    <div 
      className={`border-2 rounded-xl p-4 cursor-pointer transition-all min-w-[140px] focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 ${
        expanded ? selectedBg[action.id] : `bg-white ${bgColors[action.id]}`
      }`}
      onClick={() => onExpand(action.id)}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onExpand(action.id); }}}
      role="button"
      tabIndex={0}
      aria-expanded={expanded}
      aria-controls={expanded ? cardId : undefined}
      aria-label={`${action.title}: ${action.description}`}
    >
      <div className="text-2xl mb-2" aria-hidden="true">{action.icon}</div>
      <h4 className="font-semibold text-gray-900 text-sm">{action.title}</h4>
      <p className="text-xs text-gray-600 mt-1">{action.description}</p>
      <div className="mt-2 pt-2 border-t border-gray-200">
        <p className="text-xs font-medium text-gray-800">{action.impact_line_1}</p>
        <p className="text-xs text-gray-500">{action.impact_line_2}</p>
      </div>
      
      {expanded && (
        <div id={cardId} className="mt-4 pt-4 border-t border-gray-300" onClick={(e) => e.stopPropagation()}>
          <label htmlFor={inputId} className="block text-xs font-medium text-gray-700 mb-1">How much?</label>
          <div className="flex gap-2 items-center">
            <input
              type="number"
              id={inputId}
              value={allocateAmount}
              onChange={(e) => setAllocateAmount(Math.min(parseFloat(e.target.value) || 0, amount))}
              className="w-20 px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              max={amount}
              aria-describedby={`${inputId}-hint`}
            />
            <span id={`${inputId}-hint`} className="text-xs text-gray-500">of ${amount.toFixed(0)}</span>
          </div>
          <button
            onClick={() => onApply(action.id, allocateAmount)}
            className="mt-2 w-full py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
            aria-label={`Apply $${allocateAmount.toFixed(0)} to ${action.title}`}
          >
            Apply ${allocateAmount.toFixed(0)}
          </button>
        </div>
      )}
    </div>
  );
}

// Progress Bar Component
function ProgressBar({ score, bills, income }) {
  const billsCovered = income >= bills ? 100 : (income / bills * 100);
  
  let label = '';
  if (billsCovered >= 100) label = '✓ Fully funded';
  else if (billsCovered >= 75) label = `Almost there — ${billsCovered.toFixed(0)}%`;
  else if (billsCovered >= 50) label = `Making progress — ${billsCovered.toFixed(0)}%`;
  else label = `${billsCovered.toFixed(0)}% funded`;

  return (
    <div className="px-6 py-2 bg-gray-50 border-b border-gray-200">
      <div className="flex justify-between items-center mb-1">
        <span className="text-xs font-medium text-gray-600">{label}</span>
      </div>
      <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
        <div 
          className={`h-full rounded-full transition-all ${billsCovered >= 100 ? 'bg-green-500' : 'bg-blue-500'}`}
          style={{ width: `${Math.min(billsCovered, 100)}%` }}
        />
      </div>
    </div>
  );
}

// Future Projection Card
function ProjectionCard({ icon, title, mainValue, subtitle, details, buttonText, buttonLink, isExternal }) {
  return (
    <GlassCard className="p-5" interactive>
      <div className="flex items-center gap-2 mb-3">
        <span className="text-xl">{icon}</span>
        <h3 className="font-bold text-gray-700 text-sm uppercase tracking-wide">{title}</h3>
      </div>
      <div className="text-center mb-4">
        <p className="text-2xl font-bold text-gray-900">{mainValue}</p>
        <p className="text-sm text-gray-600">{subtitle}</p>
      </div>
      {details && (
        <div className="border-t border-gray-200/50 pt-3 space-y-1">
          {details.map((detail, i) => (
            <p key={i} className="text-sm text-gray-700">{detail}</p>
          ))}
        </div>
      )}
      {buttonText && (
        isExternal ? (
          <a 
            href={buttonLink}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-4 block w-full py-2 text-center text-sm font-medium text-emerald-600 hover:text-emerald-800 hover:bg-emerald-50 rounded-lg transition-colors"
          >
            {buttonText} ↗
          </a>
        ) : (
          <Link 
            to={buttonLink || '#'}
            className="mt-4 block w-full py-2 text-center text-sm font-medium text-emerald-600 hover:text-emerald-800 hover:bg-emerald-50 rounded-lg transition-colors"
          >
            {buttonText}
          </Link>
        )
      )}
    </GlassCard>
  );
}

function Dashboard() {
  const navigate = useNavigate();
  const [payDate, setPayDate] = useState(null);
  const [forceThree, setForceThree] = useState(false);
  const [payFrequency, setPayFrequency] = useState('biweekly');
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  const [dismissedAlerts, setDismissedAlerts] = useState([]);
  const [expandedAction, setExpandedAction] = useState({});
  const [toast, setToast] = useState(null);
  const [collapsedPeriods, setCollapsedPeriods] = useState({});  // Track collapsed pay periods
  const [isPremium, setIsPremium] = useState(false);
  
  // Streak gamification state (BrokenStreakDrama + Household Milestone)
  const [streakState, setStreakState] = useState(null);
  const [showBrokenDrama, setShowBrokenDrama] = useState(false);
  const [activeMilestone, setActiveMilestone] = useState(null);

  // Tap-Guapo quote bubble (header reaction)
  const [guapoBubble, setGuapoBubble] = useState(null);

  const handleGuapoTap = useCallback(() => {
    const q = getRandomQuote();
    setGuapoBubble(q);
    // Auto-dismiss after 3.5s
    setTimeout(() => setGuapoBubble((curr) => (curr === q ? null : curr)), 3500);
  }, []);
  
  // Celebration state
  const [showCelebration, setShowCelebration] = useState(false);
  const [celebrationType, setCelebrationType] = useState('expense');
  
  // Badge checker hook
  const { checkBadges } = useBadgeChecker();
  
  // Envelope Challenge context for real-time sync
  const { envelopeSavingsToday, myProgress, lastUpdate, hasActiveChallenge } = useEnvelope();
  
  // Quick expense state
  const [showExpenseForm, setShowExpenseForm] = useState(null); // null or periodIndex
  const [newExpense, setNewExpense] = useState({ category: 'groceries', name: '', amount: '' });
  const [addingExpense, setAddingExpense] = useState(false);
  
  // Budget advisor state
  const [showAdvisor, setShowAdvisor] = useState(false);
  const [advisorData, setAdvisorData] = useState(null);
  const [loadingAdvisor, setLoadingAdvisor] = useState(false);
  
  const settingsModified = useRef(false);

  // Load user settings on mount
  useEffect(() => {
    const loadSettings = async () => {
      try {
        const response = await apiGet('/settings');
        if (response.ok) {
          const data = await response.json();
          if (data.pay_date) {
            setPayDate(new Date(data.pay_date));
          } else {
            setPayDate(new Date());
          }
          setPayFrequency(data.pay_frequency || 'biweekly');
          setForceThree(data.force_three || false);
        } else {
          setPayDate(new Date());
        }
        
        // Check premium status - comprehensive check for all premium tiers
        const userResponse = await apiGet('/auth/me');
        if (userResponse.ok) {
          const userData = await userResponse.json();
          const PREMIUM_STATUSES = ['active', 'premium', 'family', 'household', 'lifetime_free'];
          setIsPremium(
            PREMIUM_STATUSES.includes(userData.subscription_status) || 
            userData.lifetime_free === true ||
            userData.has_access === true
          );
        }
      } catch (error) {
        console.error('Error loading settings:', error);
        setPayDate(new Date());
      } finally {
        setSettingsLoaded(true);
      }
    };

    loadSettings();
  }, []);

  // Load streak gamification state (BrokenStreakDrama + Household Milestones)
  useEffect(() => {
    const loadStreakState = async () => {
      try {
        const res = await apiGet('/user/streak-state');
        if (!res.ok) return;
        const data = await res.json();
        setStreakState(data);

        if (data.show_broken_streak_drama) {
          setShowBrokenDrama(true);
        }
        if (Array.isArray(data.pending_milestones) && data.pending_milestones.length > 0) {
          // Show the most recent unseen milestone first
          setActiveMilestone(data.pending_milestones[0]);
        }
      } catch (e) {
        console.error('Failed to load streak state:', e);
      }
    };
    loadStreakState();
  }, []);

  // Save settings when they change
  useEffect(() => {
    if (!settingsLoaded || !settingsModified.current) return;

    const saveSettings = async () => {
      setSavingSettings(true);
      try {
        await apiPut('/settings', {
          pay_date: payDate?.toISOString(),
          pay_frequency: payFrequency,
          force_three: forceThree,
        });
      } catch (error) {
        console.error('Error saving settings:', error);
      } finally {
        setSavingSettings(false);
      }
    };

    const timeoutId = setTimeout(saveSettings, 500);
    return () => clearTimeout(timeoutId);
  }, [payDate, payFrequency, forceThree, settingsLoaded]);

  const loadDashboard = useCallback(async () => {
    if (!payDate) return;
    
    try {
      const response = await apiPost('/budget/dashboard-data', {
        pay_date: payDate.toISOString(),
        force_three: forceThree,
        pay_frequency: payFrequency,
      });
      if (response.ok) {
        const data = await response.json();
        setDashboardData(data);
      }
    } catch (error) {
      console.error('Error loading dashboard:', error);
    } finally {
      setLoading(false);
    }
  }, [payDate, forceThree, payFrequency]);

  useEffect(() => {
    if (settingsLoaded && payDate) {
      loadDashboard();
    }
  }, [loadDashboard, settingsLoaded, payDate]);

  const handleDismissAlert = (alert) => {
    setDismissedAlerts([...dismissedAlerts, alert.title]);
  };

  const handleApplyAction = async (periodIndex, actionId, amount) => {
    // Get the pay period start date for this period
    const period = dashboardData?.periods?.[periodIndex];
    const payPeriodStart = period?.period_start_iso || payDate.toISOString();
    
    try {
      let endpoint = '';
      let body = {
        amount: amount,
        pay_period_start: payPeriodStart,
      };
      
      // Use specialized endpoints for each action type
      switch (actionId) {
        case 'debt':
          endpoint = '/allocations/attack-debt';
          break;
        case 'savings':
          endpoint = '/allocations/build-safety-net';
          break;
        case 'invest':
          endpoint = '/allocations/invest-for-later';
          body.note = `From Pay Period #${period?.period || periodIndex + 1}`;
          break;
        default:
          // For 'enjoy' or other, use generic allocation
          endpoint = '/allocations';
          body = {
            category: actionId,
            amount: amount,
            pay_period_start: payPeriodStart,
            note: `From Pay Period #${period?.period || periodIndex + 1}`
          };
      }
      
      const response = await apiPost(endpoint, body);
      
      if (response.ok) {
        const data = await response.json();
        
        const categoryMessages = {
          debt: data.debt_updated 
            ? `✅ $${amount.toFixed(0)} applied to ${data.debt_updated.name}! New balance: $${data.debt_updated.new_balance.toLocaleString()}`
            : `✅ $${amount.toFixed(0)} allocated to debt payoff!`,
          savings: data.goal_updated
            ? `✅ $${amount.toFixed(0)} added to ${data.goal_updated.name}! ${data.goal_updated.progress_percent.toFixed(0)}% complete!`
            : `✅ $${amount.toFixed(0)} added to your savings!`,
          invest: `✅ $${amount.toFixed(0)} allocated to investments!`,
          enjoy: `✅ $${amount.toFixed(0)} set aside for fun money!`
        };
        
        setToast({
          message: categoryMessages[actionId],
          type: 'success'
        });
        
        // Check for any new badges earned after allocation
        checkBadges();
        
        // Reload dashboard to show updated leftover
        loadDashboard();
      } else {
        setToast({
          message: '❌ Failed to save allocation. Please try again.',
          type: 'warning'
        });
      }
    } catch (error) {
      console.error('Error saving allocation:', error);
      setToast({
        message: '❌ Error saving allocation.',
        type: 'warning'
      });
    }
    
    setExpandedAction({});
  };

  // Handle "No Spend Today" button
  const handleNoSpendToday = async () => {
    try {
      const response = await apiPost('/streaks/log', {
        action_type: 'no_spend',
        description: 'No spending day logged'
      });
      
      if (response.ok) {
        const data = await response.json();
        setToast({
          message: data.message || '🎉 No spend day logged! Keep it up!',
          type: 'success'
        });
        // Check for streak-related badges
        checkBadges();
      } else {
        setToast({
          message: '❌ Failed to log no-spend day.',
          type: 'warning'
        });
      }
    } catch (error) {
      console.error('Error logging no-spend day:', error);
      setToast({
        message: '❌ Error logging no-spend day.',
        type: 'warning'
      });
    }
  };

  // Handle marking a bill as paid for a specific period (instance-level tracking)
  const handleToggleBillPaid = async (billId, isPaid, periodStartIso) => {
    try {
      const response = await apiPost('/bill-payments/toggle', { 
        bill_id: billId, 
        period_id: periodStartIso,
        is_paid: isPaid 
      });
      
      if (response.ok) {
        // Play user's preferred celebration sound
        if (isPaid) {
          playCelebration();
        }
        
        // Refresh dashboard data
        loadDashboard();
        setToast({
          message: isPaid ? '✅ Bill marked as paid!' : 'Bill marked as unpaid',
          type: 'success'
        });
      }
    } catch (error) {
      console.error('Error updating bill payment:', error);
    }
  };

  // Handle marking a debt as paid this month
  const handleToggleDebtPaid = async (debtId, isPaid) => {
    try {
      const response = await apiPut(`/debt/${debtId}`, { paid_this_month: isPaid });
      
      if (response.ok) {
        if (isPaid) {
          playCelebration();
        }
        loadDashboard();
        setToast({
          message: isPaid ? '✅ Debt payment marked as made!' : 'Debt payment marked as pending',
          type: 'success'
        });
      }
    } catch (error) {
      console.error('Error updating debt:', error);
    }
  };

  // Quick expense handlers
  const handleAddExpense = async (periodIndex) => {
    if (!newExpense.name || !newExpense.amount) return;
    
    setAddingExpense(true);
    const period = dashboardData?.periods?.[periodIndex];
    const payPeriodStart = period?.period_start_iso || payDate.toISOString();
    
    try {
      const response = await apiPost('/expenses', {
        pay_period_start: payPeriodStart,
        category: newExpense.category,
        name: newExpense.name,
        amount: parseFloat(newExpense.amount),
      });
      
      if (response.ok) {
        // Play cha-ching sound and show celebration
        playCashRegister();
        setCelebrationType('expense');
        setShowCelebration(true);
        
        setToast({
          message: `✅ Added $${parseFloat(newExpense.amount).toFixed(2)} expense for ${newExpense.name}`,
          type: 'success'
        });
        setNewExpense({ category: 'groceries', name: '', amount: '' });
        setShowExpenseForm(null);
        loadDashboard(); // Reload to update leftover
      } else {
        setToast({
          message: '❌ Failed to add expense',
          type: 'warning'
        });
      }
    } catch (error) {
      console.error('Error adding expense:', error);
      setToast({
        message: '❌ Error adding expense',
        type: 'warning'
      });
    } finally {
      setAddingExpense(false);
    }
  };

  const handleDeleteExpense = async (expenseId) => {
    try {
      const response = await apiDelete(`/expenses/${expenseId}`);
      
      if (response.ok) {
        setToast({
          message: '✅ Expense deleted',
          type: 'success'
        });
        loadDashboard();
      }
    } catch (error) {
      console.error('Error deleting expense:', error);
    }
  };

  const handleDeleteAllocation = async (allocationId) => {
    try {
      const response = await apiDelete(`/allocations/${allocationId}`);
      
      if (response.ok) {
        setToast({
          message: '✅ Allocation removed - funds returned to leftover',
          type: 'success'
        });
        loadDashboard();
      }
    } catch (error) {
      console.error('Error deleting allocation:', error);
    }
  };

  // Budget advisor handler
  const loadBudgetAdvice = async () => {
    setLoadingAdvisor(true);
    try {
      const response = await apiPost('/advisor/analyze', {
        pay_period_start: payDate?.toISOString(),
      });
      
      if (response.ok) {
        const data = await response.json();
        setAdvisorData(data);
        setShowAdvisor(true);
      } else {
        setToast({
          message: '❌ Failed to get budget advice',
          type: 'warning'
        });
      }
    } catch (error) {
      console.error('Error getting budget advice:', error);
      setToast({
        message: '❌ Error getting budget advice',
        type: 'warning'
      });
    } finally {
      setLoadingAdvisor(false);
    }
  };

  const exportToExcel = async () => {
    try {
      setToast({ message: '📊 Generating Excel export...', type: 'info' });
      
      const params = new URLSearchParams({
        pay_date: payDate.toISOString(),
        pay_frequency: payFrequency,
        force_three: forceThree.toString()
      });
      
      const response = await apiGet(`/export/budget?${params}`);
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `Budget_Export_${new Date().toISOString().split('T')[0]}.xlsx`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        
        setToast({ message: '✅ Budget exported successfully!', type: 'success' });
      } else {
        setToast({ message: '❌ Failed to export budget', type: 'warning' });
      }
    } catch (error) {
      console.error('Error exporting budget:', error);
      setToast({ message: '❌ Error exporting budget', type: 'warning' });
    }
  };

  if (loading || !settingsLoaded || !payDate) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading your budget...</p>
        </div>
      </div>
    );
  }

  const visibleAlerts = dashboardData?.alerts?.filter(a => !dismissedAlerts.includes(a.title)) || [];

  return (
    <div className="space-y-4 max-w-7xl mx-auto">
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
      
      {/* Broken Streak Drama Interceptor (Duolingo-style) */}
      <BrokenStreakDrama
        isVisible={showBrokenDrama}
        brokenStreakDays={streakState?.broken_streak_amount || 0}
        onDismiss={() => setShowBrokenDrama(false)}
      />
      
      {/* Household Milestone Celebration (friend hit 7/14/30 days) */}
      {activeMilestone && (
        <HouseholdMilestoneCelebration
          milestone={activeMilestone}
          onDismiss={() => {
            // Pop next pending milestone if any
            const remaining = (streakState?.pending_milestones || []).filter(
              (m) => m.id !== activeMilestone.id
            );
            setStreakState((s) => (s ? { ...s, pending_milestones: remaining } : s));
            setActiveMilestone(remaining[0] || null);
          }}
        />
      )}
      
      {/* Transaction Celebration Effect */}
      <TransactionCelebration 
        show={showCelebration} 
        type={celebrationType}
        onComplete={() => setShowCelebration(false)}
      />
      
      {/* Guapo Nudge - Shows for partially onboarded users */}
      <GuapoNudge />
      
      {/* Guap Board - Envelope Challenge Real-Time Widget */}
      {hasActiveChallenge && (
        <GuapBoard />
      )}
      
      {/* Section 1: Dashboard Header - More Compact */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-xl p-4 text-white">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
          <div className="flex items-center gap-3">
            <div className="relative flex-shrink-0">
              <GuapoMascot
                mode="idle"
                size="sm"
                onTap={handleGuapoTap}
                data-testid="guapo-idle-dashboard"
                className="drop-shadow-lg"
              />
              {guapoBubble && (
                <div
                  data-testid="guapo-quote-bubble"
                  className="guapo-bubble-pop absolute left-full ml-3 top-1/2 -translate-y-1/2 z-20 w-56 sm:w-64 bg-white text-gray-900 rounded-xl shadow-2xl px-3 py-2 pointer-events-none"
                  role="status"
                  aria-live="polite"
                >
                  <span className="absolute left-[-6px] top-1/2 -translate-y-1/2 w-0 h-0 border-t-[6px] border-b-[6px] border-r-[8px] border-t-transparent border-b-transparent border-r-white" />
                  <p className="text-xs sm:text-sm font-medium leading-snug">
                    "{guapoBubble.quote}"
                  </p>
                  <p className="text-[10px] text-gray-500 mt-1">— {guapoBubble.author}</p>
                </div>
              )}
            </div>
            <div>
              <div className="flex items-center gap-2 mb-1">
                <h1 className="text-2xl font-bold" data-testid="dashboard-title">
                  📊 Current Budget
                </h1>
                <QuickHelp title="How the Dashboard Works" tips={PAGE_TIPS.dashboard} variant="light" />
              </div>
              <p className="text-blue-100 text-sm">
                Your money, your plan • <Link to="/app/income" className="underline font-semibold text-white">Manage Income</Link>
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => navigate('/app/bills')}
              data-testid="edit-settings-btn"
              className="px-3 py-1.5 bg-white/20 hover:bg-white/30 text-white rounded-lg text-sm font-medium transition-colors flex items-center gap-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
              aria-label="Edit bills"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
              Edit Bills
            </button>
            <button
              onClick={exportToExcel}
              data-testid="export-budget-btn"
              className="px-3 py-1.5 bg-white/20 hover:bg-white/30 text-white rounded-lg text-sm font-medium transition-colors flex items-center gap-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
              aria-label="Export budget to Excel"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Export
            </button>
          </div>
        </div>
      </div>

      {/* Section 1.2: Global Time Message - Collapsible */}
      {dashboardData?.time_message && (
        <details className="bg-blue-50 border border-blue-200 rounded-lg group">
          <summary className="flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-blue-100 rounded-lg transition-colors list-none">
            <span className="text-lg">📅</span>
            <span className="text-blue-800 font-medium text-sm">{dashboardData.time_message}</span>
            <svg className="w-4 h-4 ml-auto text-blue-600 group-open:rotate-180 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </summary>
        </details>
      )}

      {/* Section 2: Smart Alerts Panel */}
      {visibleAlerts.length > 0 && (
        <section className="space-y-3" aria-label="Alerts">
          <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Heads Up</h2>
          {visibleAlerts.map((alert, i) => (
            <AlertCard key={i} alert={alert} onDismiss={handleDismissAlert} />
          ))}
        </section>
      )}

      {/* No Spend Today / Engagement Streak Section */}
      <GlassCard className="p-4" glow>
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
          <div className="flex items-center gap-3">
            <span className="text-2xl" aria-hidden="true">💰</span>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="font-semibold text-gray-900">Track Your Accountability</h3>
                <Popover>
                  <PopoverTrigger asChild>
                    <button 
                      className="text-gray-400 hover:text-emerald-600 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 rounded-full p-0.5"
                      aria-label="How streaks work"
                      data-testid="streak-info-btn"
                    >
                      <Info className="w-4 h-4" />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="w-80 bg-white border border-gray-200 shadow-xl" align="start">
                    <div className="space-y-3">
                      <h4 className="font-bold text-gray-900 text-sm">How to Keep Your Streak Alive 🔥</h4>
                      <p className="text-sm text-gray-600">
                        Complete at least one action daily to grow your streak:
                      </p>
                      <ul className="text-sm text-gray-700 space-y-1.5">
                        <li className="flex items-center gap-2">
                          <span className="text-emerald-500">•</span>
                          Log a "No Spend" day
                        </li>
                        <li className="flex items-center gap-2">
                          <span className="text-emerald-500">•</span>
                          Add a Quick Expense
                        </li>
                        <li className="flex items-center gap-2">
                          <span className="text-emerald-500">•</span>
                          Add a new Bill
                        </li>
                        <li className="flex items-center gap-2">
                          <span className="text-emerald-500">•</span>
                          Add or Withdraw funds in Envelopes
                        </li>
                        <li className="flex items-center gap-2">
                          <span className="text-emerald-500">•</span>
                          Add or Withdraw funds in Goals
                        </li>
                      </ul>
                      <p className="text-xs text-gray-500 pt-2 border-t border-gray-100">
                        Multiple actions in one day count as ONE day. Stay consistent!
                      </p>
                    </div>
                  </PopoverContent>
                </Popover>
              </div>
              <p className="text-sm text-gray-600">Check in with your money daily to keep your streak!</p>
            </div>
          </div>
          <button
            onClick={handleNoSpendToday}
            data-testid="no-spend-today-btn"
            className="px-4 py-2 bg-gradient-to-r from-emerald-500 to-teal-500 text-white rounded-lg font-medium hover:from-emerald-600 hover:to-teal-600 transition-all transform hover:scale-105 active:scale-95 flex items-center gap-2 shadow-lg shadow-emerald-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 focus-visible:ring-offset-2"
            aria-label="Log a no spend day"
          >
            <span aria-hidden="true">✨</span>
            No Spend Today
          </button>
        </div>
      </GlassCard>

      {/* Boss Talk Daily Quote */}
      <div className="mb-4">
        <BossTalkCard />
      </div>

      {/* Habit Praise - Shows when user has broken bad spending habits */}
      <HabitPraise className="mb-4" />

      {/* Credit Building Tips - Premium users get personalized tips */}
      {isPremium && (
        <div className="mb-4">
          <CreditTips isPremium={isPremium} />
        </div>
      )}

      {/* Section 3: Pay Period Cards - Reduced gap */}
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
        {dashboardData?.periods?.map((period, index) => {
          const adjustedIncome = period.income;
          const adjustedBills = period.total_bills;
          // True leftover includes expenses, allocations, AND envelope savings
          const totalExpenses = period.total_expenses || 0;
          const totalAllocated = period.total_allocated || 0;
          // Deduct envelope savings from today (only for current period)
          const isCurrentPeriod = index === 0; // First period is current
          const envelopeDeduction = isCurrentPeriod ? envelopeSavingsToday : 0;
          const adjustedLeftover = adjustedIncome - adjustedBills - totalExpenses - totalAllocated - envelopeDeduction;
          // Available for new allocation (before this period's allocations, just income - bills - expenses)
          const availableForAllocation = adjustedIncome - adjustedBills - totalExpenses - envelopeDeduction;
          
          // Check if this period has ended
          const periodEndDate = new Date(period.end_date_iso || period.end_date);
          const isPeriodEnded = periodEndDate < new Date();
          const isCollapsed = collapsedPeriods[index];
          
          // If collapsed, show mini card
          if (isCollapsed) {
            return (
              <div
                key={index}
                className="bg-gray-100 rounded-xl p-4 cursor-pointer hover:bg-gray-200 transition-all"
                onClick={() => setCollapsedPeriods(prev => ({ ...prev, [index]: false }))}
                data-testid={`pay-period-${index + 1}-collapsed`}
              >
                <div className="flex justify-between items-center">
                  <div>
                    <h3 className="font-bold text-gray-700">Pay #{period.period}</h3>
                    <p className="text-sm text-gray-500">{period.date} - {period.end_date}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-sm text-gray-600">
                      ${adjustedLeftover.toFixed(0)} left
                    </span>
                    <span className="text-xs bg-gray-300 text-gray-700 px-2 py-1 rounded-full">
                      Tap to expand
                    </span>
                  </div>
                </div>
              </div>
            );
          }
          
          return (
            <GlassCard
              key={index}
              className="overflow-hidden"
              interactive
              data-testid={`pay-period-${index + 1}`}
            >
              {/* Card Header */}
              <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-5">
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="text-xl font-bold">Pay #{period.period}</h3>
                    <p className="text-blue-100 mt-1">{period.date} - {period.end_date}</p>
                  </div>
                  {isPeriodEnded && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setCollapsedPeriods(prev => ({ ...prev, [index]: true }));
                      }}
                      className="text-xs bg-white/20 hover:bg-white/30 text-white px-3 py-1 rounded-full transition-colors"
                      data-testid={`collapse-period-${index + 1}`}
                    >
                      ▲ Collapse
                    </button>
                  )}
                </div>
                {isPeriodEnded && (
                  <span className="inline-block mt-2 text-xs bg-blue-500/50 px-2 py-1 rounded-full">
                    ✓ Period ended
                  </span>
                )}
              </div>

              {/* Progress Bar */}
              <ProgressBar score={period.progress_score} bills={period.total_bills} income={period.income} />

              {/* Income Section */}
              <div className="p-5 bg-green-50 border-b border-gray-200">
                <div className="flex justify-between items-center mb-3">
                  <h4 className="font-semibold text-gray-900">💵 Income</h4>
                  <Link to="/income" className="text-sm text-green-600 hover:text-green-800">Manage</Link>
                </div>
                {period.income_sources?.length > 0 ? (
                  <>
                    {period.income_sources.map((source) => (
                      <div key={source.id} className="flex justify-between text-sm mb-1">
                        <span className="text-gray-600">{source.name}</span>
                        <span className="font-medium">${source.amount.toFixed(2)}</span>
                      </div>
                    ))}
                    <div className="pt-2 border-t border-gray-300 mt-2">
                      <div className="flex justify-between font-semibold text-green-600">
                        <span>Total Income</span>
                        <span>${adjustedIncome.toFixed(2)}</span>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="text-center py-3">
                    <p className="text-gray-500 text-sm mb-2">No income added yet</p>
                    <Link to="/income" className="text-sm bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 inline-block">
                      + Add Income
                    </Link>
                  </div>
                )}
              </div>

              {/* Bills Section */}
              <div className="p-5 border-b border-gray-200">
                <h4 className="font-semibold text-gray-900 mb-3">📝 Bills ({period.bills.length})</h4>
                {period.bills.length === 0 ? (
                  <p className="text-gray-500 text-sm">No bills this period 🎉</p>
                ) : (
                  <>
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {period.bills.map((bill) => {
                        // Use period-specific payment status (paid_this_period) instead of global (paid_this_month)
                        const isPaidThisPeriod = bill.paid_this_period ?? bill.paid_this_month ?? false;
                        
                        return (
                        <div 
                          key={bill.id} 
                          className={`flex items-center justify-between text-sm p-2 rounded-lg transition-all ${
                            isPaidThisPeriod 
                              ? 'bg-green-50 border border-green-200' 
                              : bill.is_debt_payment 
                                ? 'bg-blue-50 hover:bg-blue-100 border border-blue-200'
                                : 'bg-gray-50 hover:bg-gray-100'
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            <button
                              onClick={() => {
                                if (bill.is_debt_payment) {
                                  handleToggleDebtPaid(bill.debt_id, !isPaidThisPeriod);
                                } else {
                                  // Pass period_start_iso for instance-level tracking
                                  handleToggleBillPaid(bill.id, !isPaidThisPeriod, period.period_start_iso);
                                }
                              }}
                              className={`w-6 h-6 rounded-full flex items-center justify-center transition-all border-2 ${
                                isPaidThisPeriod 
                                  ? 'bg-green-500 border-green-500 text-white' 
                                  : 'border-gray-300 text-gray-300 hover:border-green-400 hover:text-green-400'
                              }`}
                              data-testid={`bill-paid-toggle-${bill.id}-period-${index}`}
                              title={isPaidThisPeriod ? 'Mark as unpaid for this period' : 'Mark as paid for this period'}
                            >
                              {isPaidThisPeriod ? '✓' : '○'}
                            </button>
                            <div>
                              <span className={isPaidThisPeriod ? 'text-green-700 font-medium' : 'text-gray-900'}>
                                {bill.is_debt_payment ? '🎯 ' : ''}{bill.name}
                              </span>
                              {isPaidThisPeriod && (
                                <span className="ml-2 text-xs bg-green-100 text-green-700 px-1.5 py-0.5 rounded">Paid</span>
                              )}
                              {bill.is_debt_payment && !isPaidThisPeriod && (
                                <span className="ml-2 text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded">Debt</span>
                              )}
                              <span className="text-gray-500 text-xs ml-2">Due: {bill.due_date_in_period}</span>
                            </div>
                          </div>
                          <span className={`font-medium ${isPaidThisPeriod ? 'text-green-600 line-through' : ''}`}>
                            ${bill.amount.toFixed(2)}
                          </span>
                        </div>
                        );
                      })}
                    </div>
                    <div className="pt-2 border-t border-gray-300 mt-2">
                      <div className="flex justify-between font-semibold">
                        <span>Total Bills</span>
                        <span>${adjustedBills.toFixed(2)}</span>
                      </div>
                    </div>
                  </>
                )}
              </div>

              {/* ========== QUICK EXPENSES SECTION ========== */}
              <div className="p-5 border-t border-gray-200 bg-white">
                <div className="flex justify-between items-center mb-3">
                  <h4 className="font-semibold text-gray-900">🧾 Quick Expenses</h4>
                  <button
                    onClick={() => setShowExpenseForm(showExpenseForm === index ? null : index)}
                    className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                    data-testid={`add-expense-btn-${index}`}
                  >
                    + Add Expense
                  </button>
                </div>
                
                {/* Quick Expense Form */}
                {showExpenseForm === index && (
                  <div className="mb-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
                    <div className="grid grid-cols-2 gap-2 mb-2">
                      <select
                        value={newExpense.category}
                        onChange={(e) => setNewExpense({ ...newExpense, category: e.target.value })}
                        className="px-2 py-2 border border-gray-300 rounded-lg text-sm"
                      >
                        <option value="groceries">🛒 Groceries</option>
                        <option value="gas">⛽ Gas</option>
                        <option value="dining">🍔 Dining</option>
                        <option value="shopping">🛍️ Shopping</option>
                        <option value="entertainment">🎬 Entertainment</option>
                        <option value="other">📋 Other</option>
                      </select>
                      <input
                        type="number"
                        placeholder="$ Amount"
                        value={newExpense.amount}
                        onChange={(e) => setNewExpense({ ...newExpense, amount: e.target.value })}
                        className="px-2 py-2 border border-gray-300 rounded-lg text-sm"
                      />
                    </div>
                    <input
                      type="text"
                      placeholder="Description (e.g., Costco run)"
                      value={newExpense.name}
                      onChange={(e) => setNewExpense({ ...newExpense, name: e.target.value })}
                      className="w-full px-2 py-2 border border-gray-300 rounded-lg text-sm mb-2"
                    />
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleAddExpense(index)}
                        disabled={addingExpense || !newExpense.name || !newExpense.amount}
                        className="flex-1 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
                      >
                        {addingExpense ? 'Adding...' : 'Add Expense'}
                      </button>
                      <button
                        onClick={() => {
                          setShowExpenseForm(null);
                          setNewExpense({ category: 'groceries', name: '', amount: '' });
                        }}
                        className="px-4 py-2 text-gray-600 hover:text-gray-800 text-sm"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
                
                {/* Expenses List */}
                {period.expenses?.length > 0 ? (
                  <div className="space-y-2">
                    {period.expenses.map((exp) => (
                      <div key={exp.id} className="flex justify-between items-center p-2 bg-red-50 rounded-lg">
                        <div className="flex items-center gap-2">
                          <span className="text-sm">
                            {exp.category === 'groceries' ? '🛒' : 
                             exp.category === 'gas' ? '⛽' : 
                             exp.category === 'dining' ? '🍔' : 
                             exp.category === 'shopping' ? '🛍️' : 
                             exp.category === 'entertainment' ? '🎬' : '📋'}
                          </span>
                          <span className="text-sm text-gray-700">{exp.name}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-red-600">-${exp.amount.toFixed(2)}</span>
                          <button
                            onClick={() => handleDeleteExpense(exp.id)}
                            className="text-gray-400 hover:text-red-500 text-lg"
                          >
                            ×
                          </button>
                        </div>
                      </div>
                    ))}
                    <div className="flex justify-between items-center pt-2 border-t border-red-200">
                      <span className="text-sm font-medium text-gray-700">Total Expenses</span>
                      <span className="text-sm font-bold text-red-600">-${totalExpenses.toFixed(2)}</span>
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-gray-400 text-center py-2">No expenses added yet</p>
                )}
              </div>

              {/* ========== TRUE LEFTOVER SECTION ========== */}
              <div className={`p-5 border-t-4 ${adjustedLeftover >= 0 ? 'border-green-500 bg-green-50' : 'border-red-500 bg-red-50'}`}>
                <div className="text-center">
                  <span className="text-sm font-bold text-gray-700 uppercase tracking-wide">
                    💵 True Leftover - Period #{index + 1}
                  </span>
                  <p className={`text-4xl font-bold mt-2 ${adjustedLeftover >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    ${adjustedLeftover.toFixed(2)}
                  </p>
                  
                  {/* Real-time calculation breakdown */}
                  <div className="mt-4 p-3 bg-white rounded-lg text-left text-sm">
                    <div className="flex justify-between py-1">
                      <span className="text-gray-600">Income</span>
                      <span className="font-medium text-green-600">+${adjustedIncome.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between py-1">
                      <span className="text-gray-600">Bills</span>
                      <span className="font-medium text-gray-800">-${adjustedBills.toFixed(2)}</span>
                    </div>
                    {totalExpenses > 0 && (
                      <div className="flex justify-between py-1">
                        <span className="text-gray-600">Quick Expenses</span>
                        <span className="font-medium text-red-600">-${totalExpenses.toFixed(2)}</span>
                      </div>
                    )}
                    {totalAllocated > 0 && (
                      <div className="flex justify-between py-1">
                        <span className="text-gray-600">Allocated</span>
                        <span className="font-medium text-purple-600">-${totalAllocated.toFixed(2)}</span>
                      </div>
                    )}
                    {envelopeDeduction > 0 && (
                      <div className="flex justify-between py-1">
                        <span className="text-gray-600 flex items-center gap-1">
                          <span>📨</span> Envelope Saved
                        </span>
                        <span className="font-medium text-green-600">-${envelopeDeduction.toFixed(2)}</span>
                      </div>
                    )}
                    <div className="flex justify-between py-2 mt-2 border-t-2 border-gray-300 font-bold">
                      <span className="text-gray-800">= True Leftover</span>
                      <span className={adjustedLeftover >= 0 ? 'text-green-600' : 'text-red-600'}>
                        ${adjustedLeftover.toFixed(2)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* ========== ALLOCATIONS SECTION (Editable) ========== */}
              {period.allocations?.length > 0 && (
                <div className="p-5 border-t border-gray-200 bg-purple-50">
                  <div className="flex justify-between items-center mb-3">
                    <h4 className="font-semibold text-purple-900">💜 Already Allocated</h4>
                    <span className="text-sm font-bold text-purple-700">${totalAllocated.toFixed(2)}</span>
                  </div>
                  <div className="space-y-2">
                    {period.allocations.map((alloc) => (
                      <div key={alloc.id} className="flex justify-between items-center p-3 bg-white rounded-lg border border-purple-200">
                        <div className="flex items-center gap-2">
                          <span className="text-lg">
                            {alloc.category === 'debt' ? '🎯' : 
                             alloc.category === 'savings' ? '🛡️' : 
                             alloc.category === 'invest' ? '📈' : '🎉'}
                          </span>
                          <div>
                            <p className="text-sm font-medium text-gray-800 capitalize">{alloc.category}</p>
                            {alloc.note && <p className="text-xs text-gray-500">{alloc.note}</p>}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-lg font-bold text-purple-700">${alloc.amount.toFixed(0)}</span>
                          <button
                            onClick={() => handleDeleteAllocation(alloc.id)}
                            className="text-gray-400 hover:text-red-500 text-lg"
                            title="Remove allocation"
                          >
                            ×
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* ========== ACTION ENGINE - "What should this $ do?" ========== */}
              {adjustedLeftover > 0 && period.actions?.length > 0 && (
                <div className="p-5 bg-gradient-to-b from-gray-50 to-gray-100 border-t border-gray-200">
                  <h4 className="font-bold text-gray-900 mb-1 text-lg">
                    💰 What should this ${adjustedLeftover.toFixed(0)} do?
                  </h4>
                  <p className="text-xs text-gray-500 mb-4">Choose how to allocate your remaining money</p>
                  <div className="grid grid-cols-2 gap-3">
                    {period.actions.map((action) => (
                      <ActionCard
                        key={action.id}
                        action={action}
                        amount={adjustedLeftover}
                        expanded={expandedAction[`${index}-${action.id}`]}
                        onExpand={(id) => setExpandedAction({ [`${index}-${id}`]: !expandedAction[`${index}-${id}`] })}
                        onApply={(actionId, amount) => handleApplyAction(index, actionId, amount)}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Time Impact Message */}
              <div className="px-5 py-3 bg-gray-50/50 border-t border-gray-200/50">
                <p className="text-sm text-gray-600">{period.time_impact}</p>
              </div>
            </GlassCard>
          );
        })}
      </div>

      {/* Section 4: Future Projections Panel - Light Cards with Gradient Background */}
      {dashboardData?.projections && (
        <div className="mt-8">
          <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-slate-700 via-slate-600 to-slate-700 p-6">
            {/* Decorative color blocks - subtle */}
            <div className="absolute top-0 left-0 w-32 h-32 bg-blue-400/10 rounded-full blur-3xl" />
            <div className="absolute bottom-0 right-0 w-40 h-40 bg-slate-400/10 rounded-full blur-3xl" />
            
            <div className="relative z-10">
              <h2 className="text-xl font-bold text-white mb-1">🔮 Where This Path Leads</h2>
              <p className="text-sm text-gray-300 mb-6">Your current habits projected forward</p>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Debt Projection */}
                {dashboardData.projections.debt ? (
                  <div className="bg-white rounded-xl p-5 shadow-lg">
                    <div className="text-3xl mb-2">🎯</div>
                    <h3 className="text-xs font-bold text-sky-600 uppercase tracking-wider mb-1">DEBT-FREE DATE</h3>
                    <p className="text-2xl font-bold text-gray-900 mb-1">{dashboardData.projections.debt.debt_free_date}</p>
                    <p className="text-sm text-gray-500 mb-3">({dashboardData.projections.debt.months_away} months away)</p>
                    {dashboardData.projections.debt.accelerated_options?.map((opt, i) => (
                      <p key={i} className="text-xs text-gray-500">+${opt.extra}/paycheck → {opt.date} ({opt.months} mo)</p>
                    ))}
                    <Link to="/app/debt-snowball" className="mt-3 inline-block text-sm text-sky-600 hover:text-sky-700 font-medium">
                      Open Debt Calculator →
                    </Link>
                  </div>
                ) : (
                  <div className="bg-white rounded-xl p-5 shadow-lg">
                    <div className="text-3xl mb-2">🎉</div>
                    <h3 className="text-xs font-bold text-sky-600 uppercase tracking-wider mb-1">DEBT STATUS</h3>
                    <p className="text-2xl font-bold text-gray-900 mb-1">You're debt-free!</p>
                    <p className="text-sm text-gray-500 mb-3">No debt payments — that's amazing.</p>
                    <Link to="/app/debt-snowball" className="mt-3 inline-block text-sm text-sky-600 hover:text-sky-700 font-medium">
                      View Debt Calculator →
                    </Link>
                  </div>
                )}

                {/* Savings Stash */}
                <div className="bg-amber-50 rounded-xl p-5 shadow-lg">
                  <div className="text-3xl mb-2">💰</div>
                  <h3 className="text-xs font-bold text-blue-600 uppercase tracking-wider mb-1">SAVINGS STASH</h3>
                  <p className="text-2xl font-bold text-gray-900 mb-1">Building...</p>
                  <p className="text-sm text-gray-600 mb-3">Grow your savings, grow your future</p>
                  <p className="text-xs text-gray-500">Avg leftover: ${dashboardData.projections.avg_leftover}/period</p>
                  <p className="text-xs text-gray-500">Start stacking for your dreams</p>
                  <Link to="/app/goals" className="mt-3 inline-block text-sm text-blue-600 hover:text-blue-700 font-medium">
                    Set Savings Goal →
                  </Link>
                </div>

                {/* Wealth Projection */}
                {dashboardData.projections.wealth && (
                  <div className="bg-white rounded-xl p-5 shadow-lg">
                    <div className="text-3xl mb-2">📈</div>
                    <h3 className="text-xs font-bold text-emerald-600 uppercase tracking-wider mb-1">FUTURE WEALTH</h3>
                    <p className="text-2xl font-bold text-gray-900 mb-1">${dashboardData.projections.wealth.monthly_amount}/mo</p>
                    <p className="text-sm text-gray-600 mb-3">If you invest your average leftover</p>
                    <p className="text-xs text-gray-500">→ ${dashboardData.projections.wealth.year_5.toLocaleString()} in 5 years</p>
                    <p className="text-xs text-gray-500">→ ${dashboardData.projections.wealth.year_10.toLocaleString()} in 10 years</p>
                    <p className="text-xs text-gray-500">→ ${dashboardData.projections.wealth.year_20.toLocaleString()} in 20 years</p>
                    <p className="text-xs text-gray-400 mt-1">(assuming 7% annual return)</p>
                    <Link to="/app/learn-investing" className="mt-3 inline-block text-sm text-emerald-600 hover:text-emerald-700 font-medium">
                      Learn About Investing →
                    </Link>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Summary Stats */}
      {dashboardData?.summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
          <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200 text-center">
            <p className="text-sm text-gray-600">Total Income</p>
            <p className="text-2xl font-bold text-green-600">${dashboardData.summary.total_income.toLocaleString()}</p>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200 text-center">
            <p className="text-sm text-gray-600">Total Bills</p>
            <p className="text-2xl font-bold text-gray-900">${dashboardData.summary.total_bills.toLocaleString()}</p>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200 text-center">
            <p className="text-sm text-gray-600">Total Leftover</p>
            <p className={`text-2xl font-bold ${dashboardData.summary.total_leftover >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              ${Math.abs(dashboardData.summary.total_leftover).toLocaleString()}
            </p>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200 text-center">
            <p className="text-sm text-gray-600">Periods Positive</p>
            <p className="text-2xl font-bold text-blue-600">
              {dashboardData.summary.periods_positive}/{dashboardData.summary.periods_total}
            </p>
          </div>
        </div>
      )}

      {/* Security Badge - for tutorial spotlight */}
      <div 
        data-testid="security-badge"
        className="flex items-center justify-center gap-2 py-3 px-4 bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-200 rounded-xl mt-4"
      >
        <span className="text-emerald-600">🔒</span>
        <Link to="/app/security" className="text-sm text-emerald-700 hover:text-emerald-900 font-medium">
          Secured & Encrypted
        </Link>
        <span className="text-emerald-600">•</span>
        <span className="text-xs text-emerald-600">Your data is always protected</span>
      </div>

      {/* Smart Budget Advisor Button */}
      <div className="mt-6 text-center">
        <button
          onClick={loadBudgetAdvice}
          disabled={loadingAdvisor}
          className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-xl font-medium hover:from-purple-700 hover:to-indigo-700 transition-all shadow-lg disabled:opacity-50"
          data-testid="budget-advisor-btn"
        >
          {loadingAdvisor ? (
            <>
              <span className="animate-spin">⏳</span>
              Analyzing your budget...
            </>
          ) : (
            <>
              🧠 Get Smart Budget Advice
            </>
          )}
        </button>
      </div>

      {/* Budget Advisor Modal */}
      {showAdvisor && advisorData && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white p-6 rounded-t-2xl">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-bold">🧠 Smart Budget Advisor</h2>
                  <p className="text-purple-100 mt-1">AI-powered personalized advice</p>
                </div>
                <button
                  onClick={() => setShowAdvisor(false)}
                  className="text-white/80 hover:text-white text-2xl"
                >
                  ×
                </button>
              </div>
            </div>

            <div className="p-6 space-y-6">
              {/* Priority Focus */}
              <div className={`p-4 rounded-xl ${
                advisorData.advice.priority_focus === 'debt' ? 'bg-red-50 border-2 border-red-200' :
                advisorData.advice.priority_focus === 'savings' ? 'bg-blue-50 border-2 border-blue-200' :
                'bg-green-50 border-2 border-green-200'
              }`}>
                <p className="text-sm font-medium text-gray-600 mb-1">🎯 Priority Focus</p>
                <p className="text-lg font-bold capitalize">{advisorData.advice.priority_focus}</p>
              </div>

              {/* Recommended Allocation */}
              <div>
                <h3 className="font-bold text-gray-900 mb-3">📊 Recommended Allocation</h3>
                <div className="grid grid-cols-4 gap-2">
                  <div className="bg-red-100 p-3 rounded-xl text-center">
                    <p className="text-2xl font-bold text-red-700">{advisorData.advice.recommended_allocation?.debt || 0}%</p>
                    <p className="text-xs text-red-600">Debt</p>
                  </div>
                  <div className="bg-blue-100 p-3 rounded-xl text-center">
                    <p className="text-2xl font-bold text-blue-700">{advisorData.advice.recommended_allocation?.savings || 0}%</p>
                    <p className="text-xs text-blue-600">Savings</p>
                  </div>
                  <div className="bg-green-100 p-3 rounded-xl text-center">
                    <p className="text-2xl font-bold text-green-700">{advisorData.advice.recommended_allocation?.invest || 0}%</p>
                    <p className="text-xs text-green-600">Invest</p>
                  </div>
                  <div className="bg-purple-100 p-3 rounded-xl text-center">
                    <p className="text-2xl font-bold text-purple-700">{advisorData.advice.recommended_allocation?.enjoy || 0}%</p>
                    <p className="text-xs text-purple-600">Enjoy</p>
                  </div>
                </div>
              </div>

              {/* Quick Win */}
              <div className="bg-yellow-50 p-4 rounded-xl border border-yellow-200">
                <h3 className="font-bold text-yellow-800 mb-2">⚡ Quick Win</h3>
                <p className="text-yellow-900">{advisorData.advice.quick_win}</p>
              </div>

              {/* Warning if any */}
              {advisorData.advice.warning && (
                <div className="bg-red-50 p-4 rounded-xl border border-red-200">
                  <h3 className="font-bold text-red-800 mb-2">⚠️ Warning</h3>
                  <p className="text-red-900">{advisorData.advice.warning}</p>
                </div>
              )}

              {/* Positive Insight */}
              <div className="bg-green-50 p-4 rounded-xl border border-green-200">
                <h3 className="font-bold text-green-800 mb-2">💡 Insight</h3>
                <p className="text-green-900">{advisorData.advice.insight}</p>
              </div>

              {/* Your Financial Summary */}
              <div className="bg-gray-50 p-4 rounded-xl">
                <h3 className="font-bold text-gray-900 mb-3">📈 Your Summary</h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Monthly Bills:</span>
                    <span className="font-medium">${advisorData.summary?.total_bills?.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Total Debt:</span>
                    <span className="font-medium">${advisorData.summary?.total_debt?.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Goals Remaining:</span>
                    <span className="font-medium">${advisorData.summary?.goals_remaining?.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Recent Expenses:</span>
                    <span className="font-medium">${advisorData.summary?.recent_expenses?.toLocaleString()}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="p-6 border-t border-gray-200 bg-gray-50 rounded-b-2xl">
              <button
                onClick={() => setShowAdvisor(false)}
                className="w-full py-3 bg-gray-200 text-gray-800 rounded-xl font-medium hover:bg-gray-300"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Dashboard;
